import Avatar from '@components/Avatar'
import BaseModal from '@components/BaseModal'
import { ReviewInterface } from '@hermes/types/src/@types/api-legacy/bookReview'
import { Box, Button, PencilIcon, Rating, Review, SlicedLine, Typography } from '@hermes/web-components'
import useTranslateMessage from '@hooks/useTranslateMessage'
import { useUserToken } from '@hooks/useUserToken'
import { useUser } from '@lib/contexts/userContext'
import { addBookRating, addBookReview, deleteBookReview } from '@lib/services'
import getRegionFromLocale from '@lib/utils/getRegionFromLocale'
import { useState } from 'react'

import { useLocale } from '@hooks/useLocale'
import { ModalBody, ReviewTextArea } from './LeaveReviewModal.styles'

interface LeaveReviewModalProps {
  isOpen: boolean
  onClose: () => void
  editMode: boolean
  bookId: number
  review?: ReviewInterface
  updateReviews?: () => void
}

const LeaveReviewModal = ({ isOpen, onClose, editMode, bookId, review, updateReviews }: LeaveReviewModalProps) => {
  const [textAreaRows, setTextAreaRows] = useState(1)
  const [reviewTextValue, setReviewTextValue] = useState(review?.review ?? '')
  const [reviewRatingValue, setReviewRatingValue] = useState<number | null>(review?.book.rating.userRating ?? null)
  const [modalRoute, setModalRoute] = useState('enterReview')
  const { user } = useUser()
  const { token } = useUserToken()
  const locale = useLocale()
  const region = getRegionFromLocale(locale)
  const { t } = useTranslateMessage()

  const handleOnClose = () => {
    if (!editMode) {
      setReviewTextValue('')
      setReviewRatingValue(null)
      setTextAreaRows(1)
    }
    setModalRoute('enterReview')
    onClose()
  }

  const deleteReview = () =>
    deleteBookReview({
      reviewId: review!.id,
      bookId,
      token: String(token),
      country: region.toUpperCase()
    }).then(() => {
      handleOnClose()
      if (updateReviews) updateReviews()
    })

  const editReview = () => {
    Promise.all([
      addBookReview({
        review: reviewTextValue,
        reviewId: review?.id ?? undefined,
        bookId,
        token: String(token),
        country: region.toUpperCase()
      }),
      addBookRating({
        rate: reviewRatingValue!,
        token: String(token),
        bookId,
        country: region.toUpperCase()
      })
    ]).then((response) => {
      setReviewTextValue(response[0].data.review)
      setReviewRatingValue(response[1].data.rate)
      setModalRoute('editReview')
      if (updateReviews) {
        updateReviews()
        if (!editMode) handleOnClose()
      }
    })
  }

  return (
    <BaseModal isOpen={isOpen} title={t({ id: 'bdp.writeReview' })} onClose={handleOnClose}>
      {modalRoute === 'enterReview' && (
        <ModalBody>
          <Typography variant="h2" fontSize="24px" lineHeight="32px">
            {t({ id: 'modal.review.description' })}
          </Typography>
          <Rating
            className="MuiRating-large"
            max={5}
            value={reviewRatingValue}
            onChange={(e, newValue) => setReviewRatingValue(newValue)}
          />
          <ReviewTextArea
            value={reviewTextValue}
            onChange={(e) => setReviewTextValue(e.target.value)}
            label={t({ id: 'modal.review.placeholder' })}
            rows={textAreaRows}
            multiline
            onFocus={() => setTextAreaRows(6)}
            onBlur={() => !reviewTextValue && setTextAreaRows(1)}
          />
          {editMode ? (
            <Box mt="24px" display="inline-flex" gap="8px" width="100%">
              <Button onClick={deleteReview} sx={{ width: '50%' }} variant="outlined">
                {t({ id: 'delete' })}
              </Button>
              <Button
                sx={{ width: '50%' }}
                variant="contained"
                onClick={editReview}
                disabled={!(reviewTextValue.length && reviewRatingValue)}
              >
                {t({ id: 'update' })}
              </Button>
            </Box>
          ) : (
            <Button
              onClick={editReview}
              sx={{ marginTop: '24px' }}
              variant="contained"
              disabled={!(reviewTextValue.length && reviewRatingValue)}
            >
              {t({ id: 'modal.review.submit' })}
            </Button>
          )}
        </ModalBody>
      )}
      {modalRoute === 'editReview' && (
        <ModalBody>
          <Typography mb="32px" variant="h2" fontSize="24px" lineHeight="32px">
            {t({ id: 'modal.review.yourReviewSubmitted' })}
          </Typography>
          <Review
            fullName={`${user?.firstName}`}
            avatar={
              <Avatar
                src={user?.imageUrl}
                alt={user?.firstName ?? ''}
                style={{
                  borderRadius: '100%',
                  objectFit: 'cover'
                }}
                fill
              />
            }
            rating={reviewRatingValue ?? 0}
            ratingLabel={
              t(
                { id: 'review.ratingLabel' },
                {
                  value: reviewRatingValue ?? 0,
                  max: 5
                }
              ) as string
            }
            date={review?.createdAtParsed ?? ''}
            content={
              <Box maxHeight={200} sx={{ overflowY: 'auto' }}>
                <SlicedLine
                  text={reviewTextValue ?? ''}
                  max={100}
                  showMoreText={t({ id: 'showMore' }) as string}
                  hideMoreText={t({ id: 'bdp.descriptionHide' }) as string}
                  readMore
                  triggerSx={{
                    fontSize: '14px',
                    color: 'primary.main'
                  }}
                />
              </Box>
            }
            borderPrimaryColor={true}
            action={
              <Button onClick={() => setModalRoute('enterReview')} variant="icon">
                <PencilIcon width="20px" />
              </Button>
            }
          />
        </ModalBody>
      )}
    </BaseModal>
  )
}

export default LeaveReviewModal
